/*
 * REFERENCE DATA — profile wizard step 3 (canon §5.3 / §11.8).
 *
 * `REF_COUNTRIES` now covers EVERY ISO-3166-1 country (§11.8 seam — a live test
 * hit a missing country, Thailand, when the list was a curated 24). It is built
 * once at module load by MERGING two sources, curated-first:
 *
 *  1. CURATED_COUNTRIES — the 24 launch-priority markets transcribed verbatim
 *     from the DC source (App Shell.dc.html `countries()`). These carry PRECISE
 *     national-number digit rules (min/max), which are better data than the
 *     generic dataset — so they take precedence.
 *  2. `world-countries` (maintained ISO-3166 dataset) — every OTHER country,
 *     mapped into `RefCountry`. Deduped against the curated set by ISO alpha-2.
 *     For these, precise national-number length is unknown, so permissive E.164
 *     bounds (4–15) are used.
 *
 * CITY = A CLOSED LIST (Duncan ruling CMT-2026-10-07-005). The city field
 * offers ONLY the main cities of the residence country — no free text, no
 * "use my entry" option, no "request my city" button. The list is NOT held
 * here: it is the shared `services/my-api/src/data/main-cities.json`, imported
 * by BOTH this module and my-api (which enforces membership server-side on PUT
 * /me/profile), so the picker and the server can never disagree. It is
 * generated EXACTLY from datalake's MASTER CITY LIST (master_city cities.csv,
 * identified by its sha256) by `scripts/gen-main-cities.mjs`: active cities
 * only, canonical `name_en`, ordered by POPULATION (descending) within each
 * country, plus a `retired` map (old name → replacement) for names datalake
 * has retired.
 *
 * This module remains the ONLY FE place that reads the list; everything else
 * goes through its exported lookups. COUNTRY order: UAE first, then the
 * curated launch markets, then every other country A–Z by name — a
 * default-country assumption downstream depends on UAE being first.
 */
import worldCountries from 'world-countries'
import phoneMetadata from 'libphonenumber-js/metadata.min.json'
import type { Country } from 'world-countries'
import mainCities from '../../../services/my-api/src/data/main-cities.json'

export interface RefCountry {
  /** Country name, e.g. "United Kingdom". */
  n: string
  /** Demonym (nationality), e.g. "British" — the value that gets stored. */
  dem: string
  /** ISO-3166 alpha-2. */
  iso: string
  /** ISO-3166 alpha-3 (shown mono in typeahead rows). */
  iso3: string
  /** Dial code digits (no +), e.g. "971". */
  d: string
  /** National-number length rule after the country code. */
  min: number
  max: number
}

/** Transcribed from DC `countries()` — launch-priority markets with precise
 *  digit rules. Order preserved (UAE first) — this is the COUNTRY picker
 *  order. Cities come from datalake's master list (main-cities.json). */
const CURATED_COUNTRIES: RefCountry[] = [
  { n: 'United Arab Emirates', dem: 'Emirati', iso: 'AE', iso3: 'ARE', d: '971', min: 9, max: 9 },
  { n: 'Saudi Arabia', dem: 'Saudi', iso: 'SA', iso3: 'SAU', d: '966', min: 9, max: 9 },
  { n: 'Qatar', dem: 'Qatari', iso: 'QA', iso3: 'QAT', d: '974', min: 8, max: 8 },
  { n: 'Oman', dem: 'Omani', iso: 'OM', iso3: 'OMN', d: '968', min: 8, max: 8 },
  { n: 'Bahrain', dem: 'Bahraini', iso: 'BH', iso3: 'BHR', d: '973', min: 8, max: 8 },
  { n: 'Kuwait', dem: 'Kuwaiti', iso: 'KW', iso3: 'KWT', d: '965', min: 8, max: 8 },
  { n: 'United Kingdom', dem: 'British', iso: 'GB', iso3: 'GBR', d: '44', min: 10, max: 10 },
  { n: 'United States', dem: 'American', iso: 'US', iso3: 'USA', d: '1', min: 10, max: 10 },
  { n: 'France', dem: 'French', iso: 'FR', iso3: 'FRA', d: '33', min: 9, max: 9 },
  { n: 'Germany', dem: 'German', iso: 'DE', iso3: 'DEU', d: '49', min: 10, max: 11 },
  { n: 'Italy', dem: 'Italian', iso: 'IT', iso3: 'ITA', d: '39', min: 9, max: 10 },
  { n: 'Spain', dem: 'Spanish', iso: 'ES', iso3: 'ESP', d: '34', min: 9, max: 9 },
  { n: 'Netherlands', dem: 'Dutch', iso: 'NL', iso3: 'NLD', d: '31', min: 9, max: 9 },
  { n: 'India', dem: 'Indian', iso: 'IN', iso3: 'IND', d: '91', min: 10, max: 10 },
  { n: 'Pakistan', dem: 'Pakistani', iso: 'PK', iso3: 'PAK', d: '92', min: 10, max: 10 },
  { n: 'Egypt', dem: 'Egyptian', iso: 'EG', iso3: 'EGY', d: '20', min: 10, max: 10 },
  { n: 'Jordan', dem: 'Jordanian', iso: 'JO', iso3: 'JOR', d: '962', min: 9, max: 9 },
  { n: 'Lebanon', dem: 'Lebanese', iso: 'LB', iso3: 'LBN', d: '961', min: 7, max: 8 },
  { n: 'Australia', dem: 'Australian', iso: 'AU', iso3: 'AUS', d: '61', min: 9, max: 9 },
  { n: 'Canada', dem: 'Canadian', iso: 'CA', iso3: 'CAN', d: '1', min: 10, max: 10 },
  { n: 'South Africa', dem: 'South African', iso: 'ZA', iso3: 'ZAF', d: '27', min: 9, max: 9 },
  { n: 'Kenya', dem: 'Kenyan', iso: 'KE', iso3: 'KEN', d: '254', min: 9, max: 9 },
  { n: 'Japan', dem: 'Japanese', iso: 'JP', iso3: 'JPN', d: '81', min: 10, max: 10 },
  { n: 'China', dem: 'Chinese', iso: 'CN', iso3: 'CHN', d: '86', min: 11, max: 11 },
]

/** Map one `world-countries` record into a `RefCountry`. Dial code = idd.root +
 *  the first idd suffix (per §11.8 seam note), stripped of '+'. Demonym falls
 *  back to the country name when the dataset has none (permissive E.164 digit
 *  bounds, since precise lengths are unknown). */
function fromWorldCountry(c: Country): RefCountry {
  const root = c.idd?.root ?? ''
  const suffix = c.idd?.suffixes?.[0] ?? ''
  return {
    n: c.name.common,
    dem: c.demonyms?.eng?.m || c.name.common,
    iso: c.cca2,
    iso3: c.cca3,
    d: `${root}${suffix}`.replace(/\+/g, ''),
    min: 4,
    max: 15,
    ...DIAL_OVERRIDES[c.cca2],
  }
}

/** Dataset dial corrections (boardroom 2026-10-07, #297). `world-countries`
 *  splits the shared +7 code into Russia `+7` + suffix `3` and Kazakhstan `+7`
 *  + suffix `6`; root + first suffix gave the dials `73` / `76`, which are not
 *  calling codes. Both are `+7` with 10 national digits. */
const DIAL_OVERRIDES: Partial<Record<string, Pick<RefCountry, 'd' | 'min' | 'max'>>> = {
  RU: { d: '7', min: 10, max: 10 },
  KZ: { d: '7', min: 10, max: 10 },
}

/** Dial labels the picker offered before the DIAL_OVERRIDES fix → the
 *  current label. A stored legacy label still reads back as its country. */
const LEGACY_DIAL_LABELS: Record<string, string> = {
  'RU +73': 'RU +7',
  'KZ +76': 'KZ +7',
}

/** Normalise a stored dial label to the picker's current form (`ru+73` →
 *  `RU +7`); any other value passes through unchanged. */
export const canonicalDialLabel = (label: string): string => {
  const m = /^([A-Za-z]{2})\s*\+\s*(\d+)$/.exec(String(label || '').trim())
  return (m && LEGACY_DIAL_LABELS[`${m[1].toUpperCase()} +${m[2]}`]) || label
}

/** Full ISO-3166 country list in COUNTRY-PICKER order (boardroom ruling):
 *  UAE first, then the curated launch markets in their existing order, then
 *  every other country A–Z by display name (`n`, locale-aware: 'en', base
 *  sensitivity — so "Åland Islands" sorts with the A's). Built once at load. */
export const REF_COUNTRIES: RefCountry[] = (() => {
  const curatedIsos = new Set(CURATED_COUNTRIES.map((c) => c.iso))
  const rest = (worldCountries as Country[])
    .filter((c) => !curatedIsos.has(c.cca2))
    .map(fromWorldCountry)
    .sort((a, b) => a.n.localeCompare(b.n, 'en', { sensitivity: 'base' }))
  return [...CURATED_COUNTRIES, ...rest]
})()

export const countryByIso = (iso: string): RefCountry | null =>
  REF_COUNTRIES.find((c) => c.iso === iso) ?? null

/** The MAIN country (ISO alpha-2) for an international calling code, from
 *  libphonenumber's metadata (boardroom ruling 2026-10-07): a code shared by
 *  several regions lists its main region FIRST in `country_calling_codes`
 *  (`47` → NO, not Bouvet/Svalbard; `262` → RE; `1` → US; `7` → RU; `44` → GB).
 *  null when the library has no geographic entry for the code (non-geographic
 *  codes like 800 map to the pseudo-region '001'). */
export const mainIsoForCallingCode = (code: string): string | null => {
  const regions = (phoneMetadata.country_calling_codes as Record<string, string[] | undefined>)[code]
  const main = regions?.[0]
  return main && /^[A-Z]{2}$/.test(main) ? main : null
}

/** Bare dial code → its country. A code shared by several countries resolves
 *  to libphonenumber's MAIN country for it, NOT whichever REF_COUNTRIES
 *  happens to list first; falls back to the first REF_COUNTRIES entry with
 *  that exact dial (e.g. the dataset's longer dials such as `1876` → JM) when
 *  the library has no entry. A stored `ISO +dial` label's ISO wins over this —
 *  callers read the label's ISO first. */
export const countryByDial = (dial: string): RefCountry | null => {
  const main = mainIsoForCallingCode(dial)
  const byMain = main ? countryByIso(main) : null
  return byMain ?? REF_COUNTRIES.find((c) => c.d === dial) ?? null
}

/** Dial-code select options `{ISO} +{dial}`, sorted by country name (DC). */
export const dialOptions = (): string[] =>
  REF_COUNTRIES.slice()
    .sort((a, b) => a.n.localeCompare(b.n))
    .map((c) => `${c.iso} +${c.d}`)

/** Common country ABBREVIATIONS / aliases → ISO alpha-2. BOARDROOM-155042 (P0
 *  launch): a UAE user types "UAE" — which is NOT the country name ("United Arab
 *  Emirates"), the ISO2 ("AE") or the ISO3 ("ARE") — so residence/nationality
 *  search returned EMPTY, the user could not pick their country, and the city
 *  dropdown never populated → "can't find Dubai/Abu Dhabi". Alias-lowercase →
 *  ISO2. Extend freely; the event's home market (UAE) is the launch-critical one. */
const COUNTRY_ALIASES: Record<string, string> = {
  // UAE — the event's home market (BOARDROOM-155042 + 161922 expansion).
  uae: 'AE', 'u.a.e': 'AE', 'u.a.e.': 'AE', emirates: 'AE', 'the emirates': 'AE',
  // USA
  usa: 'US', 'u.s.a': 'US', 'u.s.a.': 'US', us: 'US', 'u.s': 'US', 'u.s.': 'US', america: 'US',
  // UK
  uk: 'GB', 'u.k': 'GB', 'u.k.': 'GB', britain: 'GB', 'great britain': 'GB', england: 'GB',
  // Saudi Arabia
  ksa: 'SA', saudi: 'SA',
  // Other high-traffic colloquial names (name-search catches most, but pin them).
  korea: 'KR', 'south korea': 'KR', russia: 'RU',
}

/** True when `q` is a known alias for the country (exact-alias match). */
const aliasHit = (q: string, iso: string): boolean => COUNTRY_ALIASES[q] === iso

/** Nationality typeahead: matches demonym/country/ISO2/ISO3/alias, demonym-prefix
 *  matches first, max 6 (canon §5.3; algorithm transcribed from DC). */
export function nationalityMatches(query: string): RefCountry[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const hit = REF_COUNTRIES.filter(
    (c) =>
      c.dem.toLowerCase().includes(q) ||
      c.n.toLowerCase().includes(q) ||
      c.iso.toLowerCase() === q ||
      c.iso3.toLowerCase() === q ||
      aliasHit(q, c.iso),
  )
  hit.sort(
    (a, b) =>
      (b.dem.toLowerCase().startsWith(q) ? 1 : 0) - (a.dem.toLowerCase().startsWith(q) ? 1 : 0),
  )
  return hit.slice(0, 6)
}

/** Residence typeahead: country name / ISO2 / ISO3 / common alias, max 6 (DC). */
export function residenceMatches(query: string): RefCountry[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return REF_COUNTRIES.filter(
    (c) =>
      c.n.toLowerCase().includes(q) ||
      c.iso.toLowerCase() === q ||
      c.iso3.toLowerCase() === q ||
      aliasHit(q, c.iso),
  ).slice(0, 6)
}

// ---------------------------------------------------------------------------
// City — the CLOSED main-cities list (CMT-2026-10-07-005)
// ---------------------------------------------------------------------------

/** ISO2 → main cities, from the shared main-cities.json (same file my-api
 *  validates against). Countries absent from the file have no cities. */
const MAIN_CITIES: Readonly<Record<string, readonly string[]>> = mainCities.countries

/** ISO2 → retired master-list name → its replacement (null = none). */
const RETIRED_CITIES: Readonly<Record<string, Readonly<Record<string, string | null>>>> =
  (mainCities as { retired?: Record<string, Record<string, string | null>> }).retired ?? {}

/** Matching key — the SAME fold my-api applies (profile-validation.ts
 *  foldCity): whitespace collapsed, lower-case, diacritics stripped,
 *  apostrophe variants unified. */
export const foldCity = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[\u2018\u2019\u02BC`\u00B4]/g, "'")
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()

/** The closed city list for a country, in master-list order (population
 *  descending). [] when the list has none for the country. */
export function citiesFor(country: RefCountry): string[] {
  return [...(MAIN_CITIES[country.iso] ?? [])]
}

/** City typeahead within the picked residence country: filters the closed
 *  list (accent/case-insensitive), prefix matches first. The list is short
 *  (max 15), so an empty query shows ALL of it — the user picks from what
 *  exists, never types a value. */
export function cityMatches(country: RefCountry, query: string): string[] {
  const q = foldCity(query)
  const all = citiesFor(country)
  if (!q) return all
  const hits = all.filter((c) => foldCity(c).includes(q))
  hits.sort((a, b) => (foldCity(b).startsWith(q) ? 1 : 0) - (foldCity(a).startsWith(q) ? 1 : 0))
  return hits
}

/** The canonical `name_en` of `city` for the country — the same rule my-api
 *  enforces: an active member as itself (case/space/accent-insensitive), a
 *  RETIRED master-list name as its replacement; null when the city is not in
 *  the list (e.g. a legacy free-text value) or was retired with no
 *  replacement. */
export function canonicalCity(country: RefCountry | null, city: string): string | null {
  if (!country) return null
  const t = foldCity(city)
  if (!t) return null
  const active = citiesFor(country).find((c) => foldCity(c) === t)
  if (active) return active
  const retired = RETIRED_CITIES[country.iso] ?? {}
  for (const [old, to] of Object.entries(retired)) if (foldCity(old) === t) return to
  return null
}

/** True when `city` is in the country's closed list (case-insensitive). */
export function cityKnown(country: RefCountry, city: string): boolean {
  return canonicalCity(country, city) !== null
}
